import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Box } from '../../../../wag-react/next-components/box';
import { Typography } from '../../../../wag-react/components/Typography';
import { LazyImage } from '../../../../wag-react/components/lazyload';
import { HomeBookingFilters } from '../HomeBookingFilters';
import { useFiltersStore } from '../../../BookingJourney/context/stores/booking-filters';
import { useBookingStore } from '../../../BookingJourney/context';
import { initialCaregiverValues, initialSchedule } from '../../../BookingJourney/context/stores/booking-journey';

// Define our own simplified service type
export type ServiceInfo = {
  id: string;
  name: string;
  description?: string;
  imagePath: string;
  url: string;
  additionalInfo?: string;
};

// Using Record<string, never> instead of {} for empty object type
type Props = Record<string, never>;
export const serviceItems: ServiceInfo[] = [{
  id: 'walk',
  name: 'Walking',
  description: 'Dog walking services',
  imagePath: require('../../assets/services/walk.svg'),
  url: '/dog-walking',
  additionalInfo: 'Choose a 20, 30 or 60 minutes walk. Scheduled or on-demand.'
}, {
  id: 'drop-in',
  name: 'Drop-In',
  description: 'Drop-in pet care visits',
  imagePath: require('../../assets/services/drop-in.svg'),
  url: '/drop-ins',
  additionalInfo: 'A brief home visit for pets who love having friends over'
}, {
  id: 'sitting',
  name: 'Sitting',
  description: 'In-home pet sitting services',
  imagePath: require('../../assets/services/sitting.svg'),
  url: '/dog-sitting',
  additionalInfo: 'A sleepover for your pet in the comfort of your own home'
}, {
  id: 'boarding',
  name: 'Boarding',
  description: 'Overnight boarding services',
  imagePath: require('../../assets/services/boarding.svg'),
  url: '/dog-boarding',
  additionalInfo: 'A sleepover for your pet in a Pet Caregiver’s home'
}];
const Container = styled(Box)`
  display: flex;
  width: 100%;
  max-width: 980px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 16px;
  background: #00BF8F;
  box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.20);
  margin-top: 2px;
`;
const ServiceGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const ServiceCard = styled(Box)<{
  isSelected: boolean;
}>`
  display: flex;
  padding: 0px 0px;
  flex-direction: column;
  align-items: center;
  border-radius: 12px;
  background: ${props => props.isSelected ? '#E5F9F4' : '#FFF'};
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  height: 120px;

  &:hover {
    background: ${props => props.isSelected ? '#E5F9F4' : '#F5F5F5'};
  }
`;
const ServiceImageWrapper = styled(Box)`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ServiceCardInnerWrapper = styled(Box)<{
  isSelected: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100% - 10px);
  height: calc(100% - 10px);
  margin: 5px;
  border-radius: 10px;
  border: 2px solid ${props => props.isSelected ? '#00BF8F' : 'transparent'};
  box-sizing: border-box;
`;
const CheckIndicator = styled.div`
  position: absolute;
  top:10px;
  right: 10px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url(${require('../../assets/services/check-bullet.png')});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;
const WarningIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #FF5867;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:after {
    content: '!';
    color: white;
    font-size: 16px;
    font-weight: bold;
  }
`;

// Helper function to determine if a service is on-demand
const isServiceOnDemand = (serviceId: string): boolean => {
  return serviceId === 'walk' || serviceId === 'drop-in';
};
export const WagServicesV2: React.FC<Props> = () => {
  const {
    filters,
    setFilters
  } = useFiltersStore();

  // Get the currently selected service ID from the filters store
  const selectedServiceId = filters.service || null;

  // Get the setIsMarketplaceEnabled function from the booking store
  const setIsMarketplaceEnabled = useBookingStore(state => state.setIsMarketplaceEnabled);
  const setIsOnDemand = useBookingStore(state => state.setIsOnDemand);
  const setSchedule = useBookingStore(state => state.setSchedule);
  const setCaregiver = useBookingStore(state => state.setCaregiver);
  const updateServiceFromStorage = (parsedData: any) => {
    if (parsedData?.state?.filters?.service === 'boarding') {
      const lastSelectedService = localStorage.getItem('wag-last-selected-service');
      if (lastSelectedService && lastSelectedService !== 'boarding') {
        setFilters({
          service: lastSelectedService as any
        });
        parsedData.state.filters.service = lastSelectedService;
        localStorage.setItem('wag-booking-filters', JSON.stringify(parsedData));
      }
    }
    const currentService = parsedData?.state?.filters?.service;
    if (currentService) {
      setIsMarketplaceEnabled(!isServiceOnDemand(currentService));
      setIsOnDemand(isServiceOnDemand(currentService));
    }
  };
  useEffect(() => {
    try {
      const storedData = localStorage.getItem('wag-booking-filters');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData?.state?.filters) {
          updateServiceFromStorage(parsedData);
        }
      }
    } catch (error) {
      // Do nothing, we just want to swallow the error
    }
  }, [selectedServiceId, setFilters, setIsMarketplaceEnabled, setIsOnDemand]);
  const handleServiceSelect = (serviceId: string) => {
    const scrollPos = window.scrollY;
    if (selectedServiceId === serviceId) {
      setFilters({
        service: undefined
      });
    } else {
      setFilters({
        service: serviceId as any
      });

      // CRITICAL FIX: Store the selection in a separate localStorage key as backup
      localStorage.setItem('wag-last-selected-service', serviceId);

      // Set isMarketplaceEnabled based on service type
      // For on-demand services like 'walk' or 'drop-in', set to false, otherwise true
      setIsMarketplaceEnabled(!isServiceOnDemand(serviceId));

      // Verify the update in localStorage and fix if needed
      setTimeout(() => {
        const storageKey = 'wag-booking-filters';
        const storedData = localStorage.getItem(storageKey);
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);

            // Check if we need to manually fix the localStorage
            if (parsedData?.state?.filters?.service !== serviceId) {
              parsedData.state.filters.service = serviceId;
              localStorage.setItem(storageKey, JSON.stringify(parsedData));
            }
          } catch (error) {
            // Do nothing, we just want to swallow the error
          }
        }
      }, 100);
    }

    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, scrollPos);
    }, 0);
  };

  // Add new useEffect to check and set default service
  // Initialize schedule on first load
  useEffect(() => {
    setSchedule({
      fillStrategy: 1,
      start: initialSchedule.start,
      end: initialSchedule.end
    });
    // Reset the caregiver state
    setCaregiver(initialCaregiverValues);
  }, [setSchedule]);
  useEffect(() => {
    const lastSelectedService = localStorage.getItem('wag-last-selected-service');
    if (!lastSelectedService) {
      setFilters({
        service: 'walk'
      });
      localStorage.setItem('wag-last-selected-service', 'walk');
    }
  }, [setFilters]);
  return <Container data-sentry-element="Container" data-sentry-component="WagServicesV2" data-sentry-source-file="index.tsx">
      <Typography variant="body1" color="#FFF" fontSize="18px" lineHeight="28px" letterSpacing="0px" mb="-4px" mt="0px" style={{
      fontFamily: '"Hero New", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      fontWeight: 600
    }} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
        Ready to get started? Book your first service now!
      </Typography>
      
      <ServiceGrid data-sentry-element="ServiceGrid" data-sentry-source-file="index.tsx">
        {serviceItems.map(item => {
        // Simply check if this item's ID matches the selected service ID
        const isItemSelected = item.id === selectedServiceId;
        return <ServiceCard key={item.id} isSelected={isItemSelected} onClick={() => handleServiceSelect(item.id)}>
              <ServiceCardInnerWrapper isSelected={isItemSelected}>
                <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
                  <ServiceImageWrapper>
                    <LazyImage height="48px" width="48px" src={item.imagePath} alt={`${item.name}-icon`} />
                  </ServiceImageWrapper>
                  <Typography variant="body2" mt="4px" color="#222" fontSize="16px" lineHeight="24px" style={{
                fontFamily: '"Hero New", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontWeight: 500
              }}>
                    {item.name}
                  </Typography>
                </div>
              </ServiceCardInnerWrapper>
              {isItemSelected && <CheckIndicator />}
            </ServiceCard>;
      })}
      </ServiceGrid>
      
      <HomeBookingFilters selectedService={selectedServiceId || null} data-sentry-element="HomeBookingFilters" data-sentry-source-file="index.tsx" />
    </Container>;
};
export default WagServicesV2;